import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, CheckCircle2, KeyRound, ShieldAlert } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PASSWORD_RULES_HINT } from "../components/ForgotPasswordDialog";
import { api, errMsg } from "../lib/api";

/**
 * Phase 125 — landing page for the password reset LINK sent by the `password_reset` template
 * (`/reset-password?token=…&muni=<code>` for staff / citizens, `…&scope=platform` for super admins).
 * The token is one-time and expires 10 minutes after the request.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  const muni = (params.get("muni") || "").trim().toLowerCase();
  const isPlatform = params.get("scope") === "platform";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const valid = !!token && (isPlatform || !!muni);
  const endpoint = isPlatform ? "/platform/auth/reset-password" : `/t/${muni}/auth/reset-password`;
  const loginHref = isPlatform ? "/login" : "/login";

  const submit = async (e) => {
    e.preventDefault();
    if (pw1 !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    setError("");
    try {
      const r = await api.post(endpoint, { token, new_password: pw1 });
      toast.success(r.data.message || "Password reset successfully");
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/40 flex flex-col items-center justify-center px-4 py-10" data-testid="reset-password-page">
      <div className="flex items-center gap-2 mb-6">
        <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center"><Building2 className="h-5 w-5 text-primary-foreground" /></div>
        <div>
          <p className="font-semibold font-heading leading-tight">MunicipalConnect</p>
          <p className="text-xs text-muted-foreground leading-tight">{isPlatform ? "Platform access" : muni ? `Municipality ${muni.toUpperCase()}` : "Staff & Platform Access"}</p>
        </div>
      </div>

      <Card className="w-full max-w-sm shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2"><KeyRound className="h-4 w-4" /> Choose a new password</CardTitle>
          <p className="text-sm text-muted-foreground">This link works once and expires 10 minutes after it was requested.</p>
        </CardHeader>
        <CardContent>
          {!valid ? (
            <div className="space-y-3" data-testid="reset-password-invalid">
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 mt-0.5 text-destructive" />
                <span>This reset link is incomplete. Open the link exactly as it appears in your email, SMS or WhatsApp message.</span>
              </div>
              <Button asChild variant="outline" className="w-full" data-testid="reset-password-back-to-login"><Link to={loginHref}>Back to sign in</Link></Button>
            </div>
          ) : done ? (
            <div className="space-y-4 text-center" data-testid="reset-password-success">
              <CheckCircle2 className="h-10 w-10 mx-auto text-[hsl(var(--success))]" />
              <div>
                <p className="font-medium">Password updated</p>
                <p className="text-sm text-muted-foreground">You can now sign in with your new password.</p>
              </div>
              <Button asChild className="w-full" data-testid="reset-password-go-to-login"><Link to={loginHref}>Go to sign in</Link></Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm flex items-start gap-2" data-testid="reset-password-error">
                  <ShieldAlert className="h-4 w-4 mt-0.5 text-destructive" />
                  <span>
                    {error}
                    {/* Only point to a new link when the LINK itself is the problem; password-rule errors just need a retry */}
                    {/link/i.test(error) && <> <Link to={loginHref} className="underline">Request a new link</Link> from the sign-in page.</>}
                  </span>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="rp-pw1">New password</Label>
                <Input id="rp-pw1" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} required autoFocus data-testid="reset-password-new-input" />
                <p className="text-[11px] text-muted-foreground">{PASSWORD_RULES_HINT}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rp-pw2">Confirm new password</Label>
                <Input id="rp-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-password-confirm-input" />
              </div>
              <Button type="submit" className="w-full" disabled={busy || !pw1 || !pw2} data-testid="reset-password-submit">
                <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Set new password"}
              </Button>
              <Button asChild variant="ghost" size="sm" className="w-full text-muted-foreground"><Link to={loginHref}>Back to sign in</Link></Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
